/**
 * Product Comparison structured data (JSON-LD) for SEO optimization
 * Adds Product schema for comparison pages to enhance search visibility
 */

import { config } from '@/lib/config';
import { getLogoPathForSEO } from '@/lib/constants/logo-paths';
import { JSONLDScript } from './jsonld-script';

interface ProductComparisonStructuredDataProps {
  /** Email Ferret product details */
  emailFerret: {
    price: string;
    priceCurrency?: string;
    description: string;
  };
  /** Competitor product details */
  competitor: {
    name: string;
    price: string;
    priceCurrency?: string;
    description: string;
    url?: string;
  };
  /** Page URL */
  url: string;
}

export function ProductComparisonStructuredData({
  emailFerret,
  competitor,
  url,
}: ProductComparisonStructuredDataProps) {
  const baseUrl = config.appUrl;

  // Email Ferret Product Schema
  const emailFerretProduct = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Email Ferret',
    description: emailFerret.description,
    image: {
      '@type': 'ImageObject',
      url: `${baseUrl}/og-image.png`,
      width: 1200,
      height: 630,
    },
    brand: {
      '@type': 'Brand',
      name: 'Email Ferret',
    },
    logo: `${baseUrl}${getLogoPathForSEO()}`,
    url: baseUrl,
    offers: {
      '@type': 'Offer',
      price: emailFerret.price,
      priceCurrency: emailFerret.priceCurrency || 'USD',
      availability: 'https://schema.org/InStock',
      url: `${baseUrl}/pricing`,
    },
    category: 'Email Security Software',
  };

  // Competitor Product Schema
  const competitorProduct = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: competitor.name,
    description: competitor.description,
    offers: {
      '@type': 'Offer',
      price: competitor.price,
      priceCurrency: competitor.priceCurrency || 'USD',
      availability: 'https://schema.org/InStock',
    },
    ...(competitor.url && { url: competitor.url }),
  };

  // ItemList for comparison
  const comparisonList = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: `Email Ferret vs ${competitor.name} Comparison`,
    description: `Compare Email Ferret and ${competitor.name} features, pricing, and capabilities`,
    url: url,
    numberOfItems: 2,
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        item: emailFerretProduct,
      },
      {
        '@type': 'ListItem',
        position: 2,
        item: competitorProduct,
      },
    ],
  };

  return (
    <>
      <JSONLDScript data={emailFerretProduct} id="email-ferret-product-schema" />
      <JSONLDScript data={competitorProduct} id="competitor-product-schema" />
      <JSONLDScript data={comparisonList} id="comparison-list-schema" />
    </>
  );
}
